import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { KeyboardShortcutsProvider } from "@/hooks/useKeyboardShortcuts";
import { FinoraThemeProvider } from "@/contexts/ThemeContext";
import { AmbientBackground } from "@/components/ambient/AmbientBackground";

// Lazy load all page components to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Assets = lazy(() => import("./pages/Assets"));
const Debts = lazy(() => import("./pages/Debts"));
const Savings = lazy(() => import("./pages/Savings"));
const Taxes = lazy(() => import("./pages/Taxes"));
const Reminders = lazy(() => import("./pages/Reminders"));
const Settings = lazy(() => import("./pages/Settings"));
const Tools = lazy(() => import("./pages/Tools"));
const Help = lazy(() => import("./pages/Help"));
const Auth = lazy(() => import("./pages/Auth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Pricing = lazy(() => import("./pages/Pricing"));
const TaxCountry = lazy(() => import("./pages/TaxCountry"));
const Learn = lazy(() => import("./pages/Learn"));
const Landing = lazy(() => import("./pages/Landing"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      <span className="text-muted-foreground text-sm">Loading...</span>
    </div>
  </div>
);

/* App root — ambient background must be inside FinoraThemeProvider */
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <SubscriptionProvider>
          <FinoraThemeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AmbientBackground />
                <KeyboardShortcutsProvider>
                  <Suspense fallback={<PageLoader />}>
                    <div className="relative z-10">
                    <Routes>
                      <Route path="/welcome" element={<Landing />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
                      <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                      <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                      <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
                      <Route path="/debts" element={<ProtectedRoute><Debts /></ProtectedRoute>} />
                      <Route path="/savings" element={<ProtectedRoute><Savings /></ProtectedRoute>} />
                      <Route path="/taxes" element={<ProtectedRoute><Taxes /></ProtectedRoute>} />
                      <Route path="/taxes/:country" element={<ProtectedRoute><TaxCountry /></ProtectedRoute>} />
                      <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                      <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
                      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    </div>
                  </Suspense>
                </KeyboardShortcutsProvider>
              </BrowserRouter>
            </TooltipProvider>
          </FinoraThemeProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
